import React from 'react';
import {
  Badge,
  Box,
  Button,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { Settings } from '@mui/icons-material';
import { FormattedMessage } from 'react-intl';
import { RouterOutput, trpc } from '../api/trpc';
import { Group } from '../organisms/group';
import { Message } from '../atoms/icons/message';
import { FormattedDateTime } from '../utils/formatted-datetime';
import { appMessagesTextMapping } from '../utils/app-messages.text-mapping';
import { flatten } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from '../atoms/icons/chevron-right';

import { links } from '../paths';
import { useConfirm } from 'material-ui-confirm';

type Item = RouterOutput['listAppMessages']['data'][number];

const AppMessages = () => {
  const navigate = useNavigate();

  const { isLoading, data, hasNextPage, fetchNextPage } =
    trpc.listAppMessages.useInfiniteQuery(
      { limit: 10 },
      {
        refetchInterval: 10_000,
        getNextPageParam: (lastPage) => lastPage.next,
      }
    );

  const list = flatten(flatten(data?.pages || []).map(({ data }) => data));

  if (isLoading) {
    return (
      <Group layout="list">
        <Skeleton height={72} />
        <Skeleton height={72} />
        <Skeleton height={72} />
      </Group>
    );
  }

  return (
    <Group layout="list">
      {list.map((message) => {
        return <MessageItem key={message.id} message={message} />;
      })}

      {!list.length && (
        <Box sx={{ py: 3 }}>
          <Typography variant="h5" sx={{ textAlign: 'center' }}>
            <FormattedMessage
              id="app_messages.no_messages"
              defaultMessage="Zatím zde nejsou žádné notifikace"
            />
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          pt: 1,
          pb: 2,
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
        }}
      >
        <Box />

        <Button
          onClick={() => fetchNextPage()}
          disabled={!hasNextPage || isLoading}
          variant="outlined"
        >
          <FormattedMessage
            id="app_messages.load_more"
            defaultMessage="Načíst více"
          />
        </Button>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton
            onClick={() =>
              navigate(`${links.settings}`, {
                state: { scroll: 'app-messages-settings' },
              })
            }
          >
            <Settings />
          </IconButton>
        </Box>
      </Box>
    </Group>
  );
};

const MessageItem = ({ message }: { message: Item }) => {
  const confirm = useConfirm();

  const mapping = appMessagesTextMapping[message.type];
  if (!mapping) return null;

  const { title, body } = mapping;

  const unread = false;

  const titleComponent = (
    <FormattedMessage {...title} values={message.payload} />
  );
  const atComponent = <FormattedDateTime value={message.createdAt} />;
  const bodyComponent = body ? (
    <FormattedMessage {...body} values={message.payload} />
  ) : null;

  const onOpen = () => {
    confirm({
      title: titleComponent,
      titleProps: { variant: 'h3' },
      content: (
        <>
          <Typography sx={{ color: 'text.disabled' }}>{atComponent}</Typography>
          {bodyComponent && (
            <Typography sx={{ mt: 2 }}>{bodyComponent}</Typography>
          )}
        </>
      ),
      dialogProps: {
        slotProps: { paper: { sx: { bgcolor: 'secondary.main' } } },
      },
      hideCancelButton: true,
      confirmationButtonProps: { fullWidth: true, variant: 'contained' },
      confirmationText: (
        <FormattedMessage
          id="app_messages.detail_dialog.close"
          defaultMessage="Přečteno"
        />
      ),
      dialogActionsProps: { sx: { px: 3, pb: 3 } },
    });
  };

  return (
    <Paper
      key={message.id}
      sx={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch' }}
      onClick={onOpen}
    >
      <Box sx={{ pr: 1.5 }}>
        <Badge color={unread ? 'primary' : undefined} variant="dot">
          <Message
            sx={{
              color: 'text.disabled',
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
            }}
          />
        </Badge>
      </Box>

      <Stack gap={0.5} sx={{ flex: 1 }}>
        <Typography sx={{ color: 'text.disabled' }}>{atComponent}</Typography>

        <Typography variant="body2">{titleComponent}</Typography>

        {bodyComponent && (
          <Typography sx={{ color: 'text.disabled' }}>
            {bodyComponent}
          </Typography>
        )}
      </Stack>

      <Box sx={{ textAlign: 'right' }}>
        <IconButton size="small">
          <ChevronRight sx={{ color: 'primary.main', fontSize: '0.75rem' }} />
        </IconButton>
      </Box>
    </Paper>
  );
};

export default AppMessages;
